import { useMemo } from "react";

import { PlusOutlined } from "@ant-design/icons";
import { Button, Card, Select, Space, Tag, Typography } from "antd";
import styled from "styled-components";

import { EntityKind } from "models/entityKind";

import GroupBy from "components/groupBy";
import { CustomField } from "components/sync/hooks/useFetchCustomFields";

import { useFieldSelection } from "../hooks/useFieldSelection";
import { ScenarioField } from "../types/field";

const { Text } = Typography;

const StyledCard = styled(Card)`
  margin-bottom: 24px;

  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const FieldBadgeContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const FieldItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  background-color: #fafafa;
  min-width: 200px;
`;

const StyledTag = styled(Tag)`
  padding: 6px 12px;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
`;

const CustomFieldSelect = styled(Select)`
  width: 100%;

  .ant-select-selector {
    font-size: 12px;
  }
`;

const TriggerWrapper = styled.div`
  display: inline-flex;
  cursor: pointer;
`;

interface FieldSelectionProps {
  selectedFields: ScenarioField[];
  onAddField: (field: ScenarioField) => void;
  onRemoveField: (fieldId: string) => void;
  customFields?: CustomField[];
  onCustomFieldAssign?: (fieldId: string, customFieldId: string | null) => void;
}

export const FieldSelection: React.FC<FieldSelectionProps> = ({
  selectedFields,
  onAddField,
  onRemoveField,
  customFields = [],
  onCustomFieldAssign
}) => {
  const { handleFieldSelect, handleRemoveField } = useFieldSelection({
    selectedFields,
    onAddField,
    onRemoveField
  });

  const handleCustomFieldChange = (
    fieldId: string,
    customFieldId: string | undefined | null
  ) => {
    const normalizedValue = customFieldId === undefined ? null : customFieldId;
    if (onCustomFieldAssign) {
      onCustomFieldAssign(fieldId, normalizedValue);
    }
  };

  const sortedCustomFields = useMemo(() => {
    return [...customFields].sort((a, b) => {
      const nameA = a.name ?? "";
      const nameB = b.name ?? "";
      return nameA.localeCompare(nameB);
    });
  }, [customFields]);

  return (
    <StyledCard
      title={
        <Space>
          <span>Selected Fields ({selectedFields.length})</span>
        </Space>
      }
      extra={
        <Text type="secondary">
          These fields will appear as columns in your Excel template
        </Text>
      }>
      <FieldBadgeContainer>
        {selectedFields.map((field) => (
          <FieldItemContainer key={field.field}>
            <StyledTag
              color="default"
              closable
              onClose={() => {
                handleRemoveField(field.field);
              }}>
              {field.name}
            </StyledTag>
            {customFields.length > 0 && (
              <CustomFieldSelect
                placeholder="Assign custom field"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                size="small"
                value={field.syncField?.id}
                onChange={(value: string) => handleCustomFieldChange(field.field, value)}
                options={sortedCustomFields.map((cf) => ({
                  label: cf.name,
                  value: cf.id
                }))}
              />
            )}
          </FieldItemContainer>
        ))}

        <GroupBy
          value={{ property: "", title: "Select Field" }}
          entityKind={EntityKind.Well}
          excludedCategoryFilters={[
            "Performance",
            "Midstream",
            "Forecast",
            "SAGD",
            "Sampled 3D Geo",
            "Sampled 2D Geo",
            "Organization Calculated Geo Fields",
            "Organization Geo Simulations",
            "Shapefile",
            "Parent Child",
            "Fluid Analysis",
            "Pad Metrics"
          ]}
          onChange={handleFieldSelect}>
          <TriggerWrapper>
            <Button icon={<PlusOutlined />} type="primary">
              Add Field
            </Button>
          </TriggerWrapper>
        </GroupBy>
      </FieldBadgeContainer>
    </StyledCard>
  );
};
